import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {customersApi, deploymentsApi, serversApi, sitesApi} from '../lib/api-resources';
import type {
  Customer,
  DeploymentJob,
  ListResponse,
  Server,
  Site,
  SiteCreate,
  SiteUpdate,
} from '../lib/types';
import {ApiError} from '../lib/api';
import {friendlyError} from '../lib/error-labels';
import {SITE_STATUS_LABELS} from '../lib/ui-labels';
import Modal from '../components/Modal';
import Confirm from '../components/Confirm';
import PublishModal from '../components/PublishModal';
import TemplateApplyModal from '../components/TemplateApplyModal';

const HOSTING_OPTIONS = [
  {value: '', labelKey: 'sites.filter.allHosting'},
  {value: 'CUSTOMER_SERVER', labelKey: 'sites.filter.customerServer'},
  {value: 'MANAGED_HOSTING', labelKey: 'sites.filter.managedHosting'},
];

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'muted',
  SERVER_PENDING: 'muted',
  DEPLOYING: 'warn',
  WP_INSTALLED: 'warn',
  TEMPLATE_APPLIED: 'warn',
  CONTENT_PENDING: 'warn',
  READY: 'ok',
  PUBLISHED: 'ok',
  FAILED: 'err',
  SUSPENDED: 'err',
};

const DEPLOYABLE_STATUSES: ReadonlyArray<Site['status']> = [
  'DRAFT',
  'SERVER_PENDING',
  'FAILED',
];

const PUBLISHABLE_STATUSES: ReadonlyArray<Site['status']> = [
  'WP_INSTALLED',
  'PUBLISHED',  // re-publish
];

// Phase 6: a Template can be applied once the WP backend is
// installed and reachable. We re-apply freely (the pipeline is
// idempotent on the data ids), so we also allow any post-deploy
// state to push a new apply.
const TEMPLATE_APPLYABLE_STATUSES: ReadonlyArray<Site['status']> = [
  'WP_INSTALLED',
  'TEMPLATE_APPLIED',
  'CONTENT_PENDING',
  'READY',
  'PUBLISHED',
  'TEMPLATE_APPLYING',
];

const DEPLOY_STATUS_COLORS: Record<string, string> = {
  QUEUED: 'muted',
  RUNNING: 'warn',
  SUCCESS: 'ok',
  FAILED: 'err',
  CANCELLED: 'err',
};

const STEP_STATUS_ICON: Record<string, string> = {
  PENDING: '○',
  SUCCESS: '✓',
  FAILED: '✗',
};

export default function SitesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {t} = useTranslation();
  const [list, setList] = useState<ListResponse<Site> | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [hostingFilter, setHostingFilter] = useState<'' | 'CUSTOMER_SERVER' | 'MANAGED_HOSTING'>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);
  const [deleting, setDeleting] = useState<Site | null>(null);
  const [deployJob, setDeployJob] = useState<DeploymentJob | null>(null);
  const [deployingSiteId, setDeployingSiteId] = useState<string | null>(null);
  const [publishingSite, setPublishingSite] = useState<Site | null>(null);
  const [applyingTemplateSite, setApplyingTemplateSite] = useState<Site | null>(null);
  const [busy, setBusy] = useState(false);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [initialCustomerId, setInitialCustomerId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sitesRes, customersRes, serversRes] = await Promise.all([
        sitesApi.list({
          page,
          page_size: pageSize,
          search: search || undefined,
          hosting_mode: hostingFilter || undefined,
        }),
        customersApi.list({page_size: 200}),
        serversApi.list({page_size: 200}),
      ]);
      setList(sitesRes);
      setCustomers(customersRes.items);
      setServers(serversRes.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, hostingFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    setInitialCustomerId(searchParams.get('customer_id') || '');
    setCreating(true);
    setSearchParams({}, {replace: true});
  }, [searchParams, setSearchParams]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  async function handleSave(payload: SiteCreate | SiteUpdate) {
    setBusy(true);
    try {
      if (editing) {
        await sitesApi.update(editing.id, payload as SiteUpdate);
      } else {
        await sitesApi.create(payload as SiteCreate);
      }
      setCreating(false);
      setEditing(null);
      setInitialCustomerId('');
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? friendlyError(e.code, e.message).headline : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    setBusy(true);
    try {
      await sitesApi.delete(deleting.id);
      setDeleting(null);
      await load();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Phase 6.5: open the most-recent deployment for a site.
   *
   * Fetches the latest job from the deployment history and shows
   * the detail modal. If the job is FAILED or RUNNING with a
   * stale heartbeat, the operator can click "Reconcile" inside
   * the modal to ask the backend to verify the WP side and
   * correct the control-plane state.
   */
  async function openLatestDeployment(site: Site) {
    setBusy(true);
    try {
      const hist = await sitesApi.deployments(site.id, {
        page: 1,
        page_size: 1,
      });
      const latest = hist.items[0];
      if (!latest) {
        setError(t('sites.noDeployments'));
        return;
      }
      setDeployJob(latest);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleDeploy(site: Site) {
    if (!site.server_id && site.hosting_mode !== 'MANAGED_HOSTING') {
      setError(t('sites.noServerForDeploy'));
      return;
    }
    setDeployingSiteId(site.id);
    try {
      const resp = await sitesApi.deploy(site.id);
      // Open the modal with the freshly created job
      const job = await deploymentsApi.get(resp.deployment_job_id);
      setDeployJob(job);
      setError(null);
    } catch (e) {
      const msg = e instanceof ApiError ? friendlyError(e.code, e.message).headline : String(e);
      setError(msg);
    } finally {
      setDeployingSiteId(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{t('sites.title')}</h1>
          <p className="page-subtitle">
            {list ? t('sites.countLabel', {count: list.total}) : t('common.loading')}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          {t('sites.create')}
        </button>
      </header>

      <div className="toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t('sites.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit" className="btn btn-secondary">{t('common.search')}</button>
        </form>
        <select
          value={hostingFilter}
          onChange={(e) => {
            setHostingFilter(e.target.value as '' | 'CUSTOMER_SERVER' | 'MANAGED_HOSTING');
            setPage(1);
          }}
        >
          {HOSTING_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
          ))}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('sites.table.site')}</th>
              <th>{t('common.company')}</th>
              <th>{t('sites.table.domain')}</th>
              <th>{t('sites.table.hosting')}</th>
              <th>{t('sites.table.server')}</th>
              <th>{t('sites.table.template')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.created')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="muted center">{t('common.loading')}</td>
              </tr>
            )}
            {!loading && list && list.items.length === 0 && (
              <tr>
                <td colSpan={9} className="muted center">{t('sites.empty')}</td>
              </tr>
            )}
            {list?.items.map((s) => (
              <tr key={s.id}>
                <td>
                  <div className="cell-strong">{s.name}</div>
                  <div className="cell-sub small">
                    {s.language.toUpperCase()} {s.industry && `· ${s.industry}`}
                  </div>
                </td>
                <td>{s.customer_name || '—'}</td>
                <td className="mono small">{s.domain || '—'}</td>
                <td>
                  <span className={`badge ${s.hosting_mode === 'MANAGED_HOSTING' ? 'ok' : 'muted'}`}>
                    {s.hosting_mode === 'MANAGED_HOSTING' ? t('sites.filter.managed') : t('sites.filter.customer')}
                  </span>
                </td>
                <td>
                  {s.hosting_mode === 'MANAGED_HOSTING'
                    ? '梧炅代管'
                    : (s.server_name || <span className="muted small">— {t('sites.notAssigned')}</span>)}
                </td>
                <td>
                  {s.template_id ? (
                    <code className="small">{s.template_id}@{s.template_version ?? '?'}</code>
                  ) : (
                    <span className="muted small">— {t('sites.noTemplate')}</span>
                  )}
                </td>
                <td>
                  <span className={`badge ${STATUS_COLORS[s.status] || 'muted'}`}>
                    {SITE_STATUS_LABELS[s.status] || s.status}
                  </span>
                </td>
                <td className="muted small">{s.created_at.slice(0, 10)}</td>
                <td className="actions">
                  {DEPLOYABLE_STATUSES.includes(s.status) && (s.server_id || s.hosting_mode === 'MANAGED_HOSTING') && (
                    <button
                      className="btn-link primary"
                      onClick={() => handleDeploy(s)}
                      disabled={deployingSiteId === s.id}
                    >
                      {deployingSiteId === s.id ? t('sites.deploying') : (s.status === 'DRAFT' ? '准备网站' : t('sites.deploy'))}
                    </button>
                  )}
                  {PUBLISHABLE_STATUSES.includes(s.status) && s.server_id && (
                    <button
                      className="btn-link primary"
                      onClick={() => setPublishingSite(s)}
                    >
                      {s.status === 'PUBLISHED' ? t('sites.republish') : t('common.publish')}
                    </button>
                  )}
                  {TEMPLATE_APPLYABLE_STATUSES.includes(s.status) && (
                    <button
                      className="btn-link primary"
                      onClick={() => setApplyingTemplateSite(s)}
                    >
                      {s.template_id ? t('sites.reapplyTemplate') : t('sites.applyTemplate')}
                    </button>
                  )}
                  <button
                    className="btn-link"
                    onClick={() => openLatestDeployment(s)}
                  >
                    {t('sites.lastDeploy')}
                  </button>
                  <button className="btn-link" onClick={() => setEditing(s)}>
                    {t('common.edit')}
                  </button>
                  <button
                    className="btn-link primary"
                    onClick={() => navigate(`/sites/${s.id}/ai-build`)}
                  >
                    {t('sites.buildWithAI')}
                  </button>
                  <button className="btn-link danger" onClick={() => setDeleting(s)}>
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {list && list.total_pages > 1 && (
        <div className="pager">
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← {t('common.previous')}
          </button>
          <span>
            {t('common.pageOf', {page, total: list.total_pages})}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= list.total_pages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common.next')} →
          </button>
        </div>
      )}

      {(creating || editing) && (
        <SiteFormModal
          site={editing}
          customers={customers}
          servers={servers}
          initialCustomerId={initialCustomerId}
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setInitialCustomerId('');
          }}
          onSave={handleSave}
          busy={busy}
        />
      )}

      {deleting && (
        <Confirm
          open
          title={t('sites.deleteConfirm.title')}
          message={
            <>
              {t('sites.deleteConfirm.message', {name: deleting.name})}
              <br />
              <span className="muted small">
                {t('sites.deleteConfirm.hint')}
              </span>
            </>
          }
          confirmText={t('common.delete')}
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
          busy={busy}
        />
      )}

      {deployJob && (
        <DeploymentDetailModal
          initialJob={deployJob}
          onClose={() => {
            setDeployJob(null);
            void load();  // refresh site list so the new status is visible
          }}
        />
      )}

      {publishingSite && (
        <PublishModal
          site={publishingSite}
          onClose={() => setPublishingSite(null)}
          onPublished={async (jobId) => {
            setPublishingSite(null);
            // Open the deployment detail modal with the running publish job
            try {
              const job = await deploymentsApi.get(jobId);
              setDeployJob(job);
              void load();
            } catch (e) {
              const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
              setError(msg);
            }
          }}
        />
      )}

      {applyingTemplateSite && (
        <TemplateApplyModal
          siteId={applyingTemplateSite.id}
          onClose={() => setApplyingTemplateSite(null)}
          onApplied={async (jobId) => {
            setApplyingTemplateSite(null);
            try {
              const job = await deploymentsApi.get(jobId);
              setDeployJob(job);
              void load();
            } catch (e) {
              const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
              setError(msg);
            }
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// Deployment detail modal — auto-polls while job is QUEUED/RUNNING
// =====================================================================
function DeploymentDetailModal({
  initialJob,
  onClose,
}: {
  initialJob: DeploymentJob;
  onClose: () => void;
}) {
  const {t} = useTranslation();
  const [job, setJob] = useState<DeploymentJob>(initialJob);
  const [pollError, setPollError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  // Poll every 2s while the job hasn't reached a terminal state
  useEffect(() => {
    function isTerminal(s: DeploymentJob['status']) {
      return s === 'SUCCESS' || s === 'FAILED' || s === 'CANCELLED';
    }
    async function tick() {
      try {
        const fresh = await deploymentsApi.get(job.id);
        setJob(fresh);
        setPollError(null);
        if (isTerminal(fresh.status)) {
          if (timer.current !== null) {
            window.clearTimeout(timer.current);
            timer.current = null;
          }
        } else {
          timer.current = window.setTimeout(tick, 2000);
        }
      } catch (e) {
        const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
        setPollError(msg);
        // Keep polling even on transient errors
        timer.current = window.setTimeout(tick, 4000);
      }
    }
    if (!isTerminal(job.status)) {
      timer.current = window.setTimeout(tick, 1500);
    }
    return () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const duration = (() => {
    if (!job.started_at) return null;
    const start = new Date(job.started_at).getTime();
    const end = job.finished_at ? new Date(job.finished_at).getTime() : Date.now();
    return Math.max(0, Math.round((end - start) / 1000));
  })();

  return (
    <Modal
      open
      title={t('sites.deployment.title', {id: job.id.slice(0, 8)})}
      onClose={onClose}
      size="lg"
      footer={
        <button className="btn btn-secondary" onClick={onClose}>
          {t('sites.deployment.close')}
        </button>
      }
    >
      <div className="deploy-detail">
        <div className="deploy-summary">
          <div>
            <span className="muted small">{t('sites.deployment.status')}</span>
            <div>
              <span className={`badge ${DEPLOY_STATUS_COLORS[job.status] || 'muted'}`}>
                {job.status}
              </span>
              {job.current_step && job.status === 'RUNNING' && (
                <span className="muted small"> · {t('sites.deployment.runningStep', {step: job.current_step})}</span>
              )}
            </div>
          </div>
          <div>
            <span className="muted small">{t('sites.deployment.progress')}</span>
            <div>
              <progress
                value={job.progress}
                max={100}
                style={{width: 160, height: 8}}
              />
              <span className="muted small"> {job.progress}%</span>
            </div>
          </div>
          {duration !== null && (
            <div>
              <span className="muted small">{t('sites.deployment.duration')}</span>
              <div>{duration}s</div>
            </div>
          )}
          {job.error_code && (
            <div className="deploy-error">
              <span className="muted small">{t('sites.deployment.error')}</span>
              <div className="err">
                <code>{job.error_code}</code>
                {job.error_message && <div className="small">{job.error_message}</div>}
              </div>
            </div>
          )}
        </div>

        {pollError && (
          <div className="alert alert-warn small">
            {t('sites.deployment.pollError', {error: pollError})}
          </div>
        )}

        <h4 className="deploy-steps-title">{t('sites.deployment.stepsTitle')}</h4>
        <ol className="deploy-steps">
          {job.steps.length === 0 && (
            <li className="muted small">{t('sites.deployment.noSteps')}</li>
          )}
          {job.steps.map((s) => (
            <li key={s.id} className={`deploy-step step-${s.status.toLowerCase()}`}>
              <span className="step-icon">{STEP_STATUS_ICON[s.status] || '?'}</span>
              <span className="step-name">{s.step}</span>
              <span className="step-status">
                {s.status}
                {s.duration_ms > 0 && <> · {s.duration_ms}ms</>}
              </span>
              {s.error_code && (
                <div className="step-error small err">
                  {s.error_code}
                  {s.error_message && <> — {s.error_message}</>}
                </div>
              )}
              {s.message && !s.error_code && (
                <div className="step-message small muted">{s.message}</div>
              )}
            </li>
          ))}
        </ol>

        {job.result && Object.keys(job.result).length > 0 && (
          <details className="deploy-result">
            <summary>{t('sites.deployment.resultJson')}</summary>
            <pre>{JSON.stringify(job.result, null, 2)}</pre>
          </details>
        )}
      </div>
    </Modal>
  );
}

function SiteFormModal({
  site,
  customers,
  servers,
  initialCustomerId,
  onClose,
  onSave,
  busy,
}: {
  site: Site | null;
  customers: Customer[];
  servers: Server[];
  initialCustomerId: string;
  onClose: () => void;
  onSave: (p: SiteCreate | SiteUpdate) => Promise<void>;
  busy: boolean;
}) {
  const {t} = useTranslation();
  const [form, setForm] = useState({
    customer_id: site?.customer_id ?? initialCustomerId,
    server_id: site?.server_id ?? '',
    name: site?.name ?? '',
    domain: site?.domain ?? '',
    language: site?.language ?? 'zh',
    industry: site?.industry ?? '',
    hosting_mode: (site?.hosting_mode ?? 'CUSTOMER_SERVER') as 'CUSTOMER_SERVER' | 'MANAGED_HOSTING',
    status: (site?.status ?? 'DRAFT') as Site['status'],
    clear_server: false,
  });

  function update<K extends keyof typeof form>(key: K, v: (typeof form)[K]) {
    setForm((f) => ({...f, [key]: v}));
  }

  // When customer changes, optionally suggest a server
  const customerServers = servers.filter(
    (s) => s.ownership === 'CUSTOMER' && s.customer_id === form.customer_id
  );
  const serverOptions = customerServers;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (site) {
      const update: SiteUpdate = {
        name: form.name,
        domain: form.domain || null,
        language: form.language,
        industry: form.industry || null,
        hosting_mode: form.hosting_mode,
      };
      if (form.server_id) {
        update.server_id = form.server_id;
      } else if (form.clear_server) {
        update.clear_server = true;
      }
      await onSave(update);
    } else {
      const create: SiteCreate = {
        customer_id: form.customer_id,
        name: form.name,
        domain: form.domain || null,
        language: form.language,
        industry: form.industry || null,
        hosting_mode: form.hosting_mode,
        status: form.status,
      };
      if (form.server_id) {
        create.server_id = form.server_id;
      }
      await onSave(create);
    }
  }

  return (
    <Modal
      open
      title={site ? t('sites.form.editTitle', {name: site.name}) : t('sites.form.newTitle')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t('sites.form.cancel')}
          </button>
          <button
            className="btn btn-primary"
            form="site-form"
            type="submit"
            disabled={
              busy ||
              !form.name.trim() ||
              !form.customer_id ||
              (form.hosting_mode === 'CUSTOMER_SERVER' && !form.server_id && !site)
            }
          >
            {busy ? t('sites.form.saving') : t('sites.form.save')}
          </button>
        </>
      }
    >
      <form id="site-form" onSubmit={submit} className="form-grid">
        <label>
          <span>{t('sites.form.customer')} *</span>
          <select
            value={form.customer_id}
            onChange={(e) => update('customer_id', e.target.value)}
            required
            disabled={!!site}
          >
            <option value="">{t('sites.form.selectCustomer')}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t('sites.form.hostingMode')} *</span>
          <select
            value={form.hosting_mode}
            onChange={(e) => update('hosting_mode', e.target.value as 'CUSTOMER_SERVER' | 'MANAGED_HOSTING')}
            disabled={!!site}
          >
            <option value="CUSTOMER_SERVER">{t('sites.form.customerOwnServer')}</option>
            <option value="MANAGED_HOSTING">{t('sites.form.managedHosting')}</option>
          </select>
        </label>
        <label className="full">
          <span>{t('sites.form.siteName')} *</span>
          <input
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            required
            maxLength={200}
          />
        </label>
        <label>
          <span>{t('sites.form.domain')}</span>
          <input
            value={form.domain}
            onChange={(e) => update('domain', e.target.value)}
            placeholder={t('sites.form.domainPlaceholder')}
            maxLength={255}
          />
          {form.domain.trim() && (
            <span className="muted small">保存后网站地址：https://{form.domain.trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '')}</span>
          )}
        </label>
        <label>
          <span>{t('sites.form.language')}</span>
          <select
            value={form.language}
            onChange={(e) => update('language', e.target.value)}
          >
            <option value="zh">zh (中文)</option>
            <option value="en">en (English)</option>
            <option value="ja">ja (日本語)</option>
            <option value="es">es (Español)</option>
            <option value="de">de (Deutsch)</option>
            <option value="fr">fr (Français)</option>
            <option value="ru">ru (Русский)</option>
            <option value="ar">ar (العربية)</option>
          </select>
        </label>
        <label>
          <span>{t('common.industry')}</span>
          <input
            value={form.industry}
            onChange={(e) => update('industry', e.target.value)}
            maxLength={100}
          />
        </label>
        {form.hosting_mode === 'MANAGED_HOSTING' ? (
          <div className="field full">
            <span className="field-label">托管方式</span>
            <div className="field-input" aria-readonly="true">梧炅代管（系统自动分配）</div>
            <span className="muted small">系统只会选择已启用、健康、允许部署且容量可用的托管节点。</span>
          </div>
        ) : (
          <label className="full">
            <span>{t('common.server')} *</span>
            <select
              value={form.server_id}
              onChange={(e) => update('server_id', e.target.value)}
              disabled={!form.customer_id}
              required={!site}
            >
              <option value="">— {t('sites.notAssigned')} —</option>
              {serverOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {serverOptions.length === 0 && form.customer_id && (
              <span className="muted small">{t('sites.noCustomerServer')}</span>
            )}
          </label>
        )}
        {site && form.server_id && (
          <label className="full">
            <input
              type="checkbox"
              checked={form.clear_server}
              onChange={(e) => update('clear_server', e.target.checked)}
            />
            <span> {t('sites.detachServer')}</span>
          </label>
        )}
        {site && (
          <label>
            <span>{t('common.status')}</span>
            <select
              value={form.status}
              onChange={(e) => update('status', e.target.value as Site['status'])}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="SERVER_PENDING">SERVER_PENDING</option>
              <option value="DEPLOYING">DEPLOYING</option>
              <option value="WP_INSTALLED">WP_INSTALLED</option>
              <option value="TEMPLATE_APPLIED">TEMPLATE_APPLIED</option>
              <option value="CONTENT_PENDING">CONTENT_PENDING</option>
              <option value="READY">READY</option>
              <option value="PUBLISHED">PUBLISHED</option>
              <option value="FAILED">FAILED</option>
              <option value="SUSPENDED">SUSPENDED</option>
            </select>
          </label>
        )}
        <p className="full muted small">
          {t('sites.phaseHint')}
        </p>
      </form>
    </Modal>
  );
}
